export type TutorialSidebarTab = 'palette' | 'info' | 'pages';
export type TutorialDropdown = 'new' | 'file' | 'tools';

export interface TutorialStep {
  id: string;
  title: string;
  body: string;
  /** Matches `[data-tour="…"]`. Null = centered bubble, no spotlight. */
  target: string | null;
  keys?: string[];
  chord?: boolean;
  sidebarTab?: TutorialSidebarTab;
  openDropdown?: TutorialDropdown;
  /** Expand the top-right menu so File / Tools targets exist. */
  expandToolbar?: boolean;
}

/**
 * Full first-run tour. Steps that need a DOM target fall back to a centered
 * bubble if the element is missing (view-only, sequence page, chrome hidden).
 */
export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to VeloStruct',
    body: 'A two-minute tour of the editor. You can keep working while this is open — Next when you are ready, or Skip to explore on your own.',
    target: 'header',
  },
  {
    id: 'canvas',
    title: 'Move around the canvas',
    body: 'Scroll to zoom. Hold Space and drag to pan. Shift-drag to marquee-select. Shift+Ctrl-drag for a lasso (it closes itself).',
    target: 'canvas',
    keys: ['Space', 'drag'],
  },
  {
    id: 'start',
    title: 'Start a page',
    body: 'New pages offer a C4 workspace (shared catalog across Context, Containers, Sequence), a Mermaid paste, or a blank canvas.',
    target: 'empty-state',
  },
  {
    id: 'palette',
    title: 'Palette — add nodes',
    body: 'Drag a type onto the canvas, or click it to drop one. Architecture types live here; Draw tools (box, text, arrow, frame) sit below.',
    target: 'sidebar-palette',
    sidebarTab: 'palette',
    keys: ['a', 's'],
    chord: true,
  },
  {
    id: 'chords',
    title: 'Chords are faster',
    body: 'Press a, then a letter: s Service, d Database, a API, p Person, q Queue, e External. The hint bar at the bottom lists options after the first key.',
    target: 'sidebar',
    sidebarTab: 'palette',
    keys: ['a', 's'],
    chord: true,
  },
  {
    id: 'connect',
    title: 'Connect nodes',
    body: 'Drag from an output socket (or the node body) to another node. You do not have to hit the socket exactly. Esc cancels a connection in progress.',
    target: 'canvas',
  },
  {
    id: 'info',
    title: 'Info panel',
    body: 'Tap I to open details for the node under the cursor. Hold I to inspect packed info, links, and catalog names without opening a panel.',
    target: 'sidebar-info',
    sidebarTab: 'info',
    keys: ['I'],
  },
  {
    id: 'pages',
    title: 'Pages',
    body: 'A project can have many pages (flow, sequence, or C4). The Pages tab lists them. Ctrl+[ and Ctrl+] walk history; H jumps home.',
    target: 'sidebar-pages',
    sidebarTab: 'pages',
    keys: ['Ctrl', '['],
  },
  {
    id: 'nav',
    title: 'Page history',
    body: 'These arrows go back and forward through pages you have visited — same as Ctrl+[ / Ctrl+].',
    target: 'page-nav',
    keys: ['Ctrl', '['],
  },
  {
    id: 'layers',
    title: 'Layers',
    body: 'New nodes land on the active layer. Click a row to activate it, eye to hide, ◎ to dim. l then n / h / d does the same from the keyboard.',
    target: 'layers',
    keys: ['l', 'n'],
    chord: true,
  },
  {
    id: 'group',
    title: 'Architecture groups',
    body: 'Select two or more nodes, then Ctrl+G (or the Group button). That creates a real subgraph. Tab enters it; Tab again exits. This is what AI and C4 should use.',
    target: 'toolbar',
    expandToolbar: true,
    keys: ['Ctrl', 'G'],
  },
  {
    id: 'frames',
    title: 'Frames are only boxes',
    body: 'Hold B and drag (or Draw Frame) for a labeled annotation around nodes. Frames do not nest a graph. Use Ctrl+G when you mean architecture.',
    target: 'draw-frame',
    sidebarTab: 'palette',
    keys: ['B'],
  },
  {
    id: 'draw',
    title: 'Sketch tools',
    body: 'Click a draw tool once to use it once; click again to keep drawing. d then b draws a box, d d then b keeps the tool on. Arrow and line are annotation-only strokes.',
    target: 'draw-tools',
    sidebarTab: 'palette',
    keys: ['2'],
  },
  {
    id: 'sequence',
    title: 'Sequence diagrams',
    body: 'New → Sequence (or a C4 Sequence page). Add participants, then drag on a lifeline to create a message. a then p adds a participant. Copy/cut/paste works on messages.',
    target: 'toolbar-new',
    expandToolbar: true,
    openDropdown: 'new',
    keys: ['a', 'p'],
    chord: true,
  },
  {
    id: 'c4',
    title: 'C4 workspace',
    body: 'New → C4 workspace builds Context, Containers, and Sequence pages that share one catalog. Reuse the same component identity across views.',
    target: 'toolbar-new',
    expandToolbar: true,
    openDropdown: 'new',
  },
  {
    id: 'command',
    title: 'Command palette',
    body: 'Ctrl+K jumps to a page, node, catalog entry, C4 start, or draw tool. Fastest way to move around a large project.',
    target: 'canvas',
    keys: ['Ctrl', 'K'],
  },
  {
    id: 'file',
    title: 'File menu',
    body: 'Save and load .velostruct JSON, publish a view-only link, export PNG or SVG. The format is AI-friendly: links use socket indices, not internal IDs.',
    target: 'toolbar-file',
    expandToolbar: true,
    openDropdown: 'file',
  },
  {
    id: 'ai',
    title: 'AI chat',
    body: 'The AI panel can add nodes, groups, and pages from a description. Ctrl+Shift+A toggles it. Prefer createGroup over frames when asking it to structure a diagram.',
    target: 'ai-toggle',
    keys: ['Ctrl', 'Shift', 'A'],
  },
  {
    id: 'color',
    title: 'Color and edge style',
    body: 'c then c opens the color picker for the selection. Select edges, then t then a to animate. Ctrl++ / Ctrl+- change thickness; - cycles dash; = cycles arrow type.',
    target: 'canvas',
    keys: ['c', 'c'],
    chord: true,
  },
  {
    id: 'shortcuts',
    title: 'Full shortcut list',
    body: 'Tools → Keyboard Shortcuts lists every chord. When you click something that has a shortcut, a tiny corner nudge will remind you — it never blocks the canvas.',
    target: 'toolbar-tools',
    expandToolbar: true,
    openDropdown: 'tools',
  },
  {
    id: 'status',
    title: 'Status bar',
    body: 'Zoom, counts, and selection live here. Delete removes the selection; Ctrl+Z / Ctrl+Shift+Z undo and redo. You are ready to draw.',
    target: 'status-bar',
    keys: ['Ctrl', 'Z'],
  },
];
